import { useState } from "react";
import { Mail, Globe, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const initialForm = { name: "", company: "", email: "", phone: "", product: "General enquiry" };

const inputCls =
  "w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-[#0F172A] placeholder:text-slate-400 outline-none transition-colors focus:border-[#F59E0B] focus:ring-2 focus:ring-[#F59E0B]/20";

export const ContactSection = ({ products }) => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await api.post("/leads", form);
      toast.success("Enquiry sent — our team will reach out shortly.");
      setForm(initialForm);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="lead-generation-section" className="mx-auto max-w-7xl px-6 pb-28">
      <div className="grid overflow-hidden rounded-[2rem] bg-[#0F172A] lg:grid-cols-2">
        <div className="relative p-10 sm:p-14">
          <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-amber-400/20 blur-[100px]" />
          <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F59E0B]">
            Contact
          </span>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            Let&apos;s build your next system.
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-slate-300">
            Tell us what your operation needs — a demo, pricing, or a custom deployment of any
            Smart-In product. We reply to every enquiry.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href="mailto:info@smrtin.net"
              data-testid="contact-email-link"
              className="flex items-center gap-3.5 text-slate-300 transition-colors hover:text-white"
            >
              <span className="grid h-10 w-10 place-items-center rounded-lg bg-white/10">
                <Mail className="h-5 w-5 text-[#F59E0B]" />
              </span>
              <span className="text-sm font-semibold">info@smrtin.net</span>
            </a>
            <div className="flex items-center gap-3.5 text-slate-300">
              <span className="grid h-10 w-10 place-items-center rounded-lg bg-white/10">
                <Globe className="h-5 w-5 text-[#F59E0B]" />
              </span>
              <span className="text-sm font-semibold">smrtin.net</span>
            </div>
          </div>
        </div>

        <div className="bg-white p-10 sm:p-14">
          <form onSubmit={submit} className="space-y-5" data-testid="lead-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="lead-name" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">
                  Name
                </label>
                <input
                  id="lead-name"
                  data-testid="lead-form-name"
                  className={inputCls}
                  placeholder="Jane Doe"
                  value={form.name}
                  onChange={set("name")}
                  required
                  minLength={2}
                />
              </div>
              <div>
                <label htmlFor="lead-company" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">
                  Company
                </label>
                <input
                  id="lead-company"
                  data-testid="lead-form-company"
                  className={inputCls}
                  placeholder="Acme Trading Co."
                  value={form.company}
                  onChange={set("company")}
                  required
                  minLength={2}
                />
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="lead-email" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">
                  Email
                </label>
                <input
                  id="lead-email"
                  data-testid="lead-form-email"
                  type="email"
                  className={inputCls}
                  placeholder="jane@company.com"
                  value={form.email}
                  onChange={set("email")}
                  required
                />
              </div>
              <div>
                <label htmlFor="lead-phone" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">
                  Phone
                </label>
                <input
                  id="lead-phone"
                  data-testid="lead-form-phone"
                  type="tel"
                  className={inputCls}
                  placeholder="+964 ..."
                  value={form.phone}
                  onChange={set("phone")}
                  required
                  minLength={5}
                />
              </div>
            </div>

            <div>
              <label htmlFor="lead-product" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">
                Product of interest
              </label>
              <select
                id="lead-product"
                data-testid="lead-form-product-select"
                className={inputCls}
                value={form.product}
                onChange={set("product")}
              >
                <option value="General enquiry">General enquiry</option>
                {products.map((p) => (
                  <option key={p.slug} value={p.title}>
                    {p.title}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              data-testid="lead-form-submit-btn"
              disabled={sending}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#F59E0B] px-7 py-3.5 text-sm font-bold text-[#0F172A] transition-all hover:bg-[#d97706] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  Send Enquiry
                  <Send className="h-4 w-4" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};
